import { EventCouponResponseType } from 'services/events/evetns.api.type';
import initialApiService from 'services/initialApiService';

import {
  CloneCouponType,
  CouponBatchReqType,
  ReqCouponType,
  ResCouponType,
} from 'types/coupons/couponTypes';

const couponApiService = initialApiService.injectEndpoints({
  endpoints: (builder) => ({
    getCoupon: builder.query<ResCouponType, string>({
      query: (couponId) => ({
        url: `coupons/${couponId}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'CouponDetail', id }],
    }),
    exportCoupons: builder.query<{ fileName: string }, string>({
      query: (id) => ({
        url: `coupons/export/${id}`,
        method: 'GET',
      }),
    }),
    batchCoupons: builder.mutation<{ numberOfCouponsAffected: number }, CouponBatchReqType>({
      query: (data) => ({
        url: `/coupons/batchupdate`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: [{ type: 'CouponList', id: 'LIST' }],
    }),
    getCouponsList: builder.query<
      EventCouponResponseType,
      {
        eventId: string;
        pageNumber?: number;
        pageSize?: number;
        showDisabled?: boolean;
      }
    >({
      query: (body) => ({
        url: `coupons/list/${body.eventId}`,
        method: 'GET',
        params: {
          pageNumber: body.pageNumber,
          pageSize: body.pageSize,
          showDisabled: body.showDisabled,
        },
      }),
      providesTags: () => [{ type: 'CouponList', id: 'LIST' }],
    }),
    createCoupon: builder.mutation<ResCouponType, ReqCouponType>({
      query: (data) => ({
        url: `/coupons/create`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'CouponList', id: 'LIST' }],
    }),
    editCoupon: builder.mutation<ResCouponType, ReqCouponType>({
      query: (data) => ({
        url: `/coupons/${data.id}/edit`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'CouponList', id: 'LIST' },
        { type: 'CouponDetail', id: arg.id as string },
      ],
    }),
    cloneCoupon: builder.mutation<ResCouponType, CloneCouponType>({
      query: (data) => ({
        url: `/coupons/${data.couponId}/clone`,
        method: 'PATCH',
        body: { qty: data.qty },
      }),
      invalidatesTags: [{ type: 'CouponList', id: 'LIST' }],
    }),
    disableCoupon: builder.mutation<ResCouponType, { couponId: string }>({
      query: (data) => ({
        url: `/coupons/${data.couponId}/disable`,
        method: 'PATCH',
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'CouponList', id: 'LIST' },
        { type: 'CouponDetail', id: arg.couponId },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          couponApiService.util.updateQueryData('getCoupon', arg.couponId, (draft) => {
            // Assuming ResCouponType has an 'isDisabled' field.
            // If not, this needs to be adjusted to the actual status field.
            draft.disabled = true;
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    enableCoupon: builder.mutation<ResCouponType, { couponId: string }>({
      query: (data) => ({
        url: `/coupons/${data.couponId}/enable`,
        method: 'PATCH',
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'CouponList', id: 'LIST' },
        { type: 'CouponDetail', id: arg.couponId },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          couponApiService.util.updateQueryData('getCoupon', arg.couponId, (draft) => {
            // Assuming ResCouponType has an 'isDisabled' field.
            draft.disabled = false;
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    deleteCoupon: builder.mutation<ResCouponType, { couponId: string }>({
      query: (data) => ({
        url: `/coupons/${data.couponId}`,
        method: 'DELETE',
      }),
    }),
  }),
});

export const {
  useLazyExportCouponsQuery,
  useBatchCouponsMutation,
  useCreateCouponMutation,
  useLazyGetCouponsListQuery,
  useEditCouponMutation,
  useLazyGetCouponQuery,
  useCloneCouponMutation,
  useDisableCouponMutation,
  useEnableCouponMutation,
  useDeleteCouponMutation,
} = couponApiService;
